import { ArrowDown, ArrowUpRight } from "lucide-react";
import type { HubHeroProps } from "@/types/help-hub/presentation";
import { deepen, heroBackground, hubBrandTheme, tint } from "../brandInk";
import { HubAskBox } from "./HubAskBox";
import { HubChannels } from "./HubChannels";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import "../help-hub.css";

/**
 * The shared support-first entrance. Real questions and contact methods only;
 * the layout needs no photography, customer-specific copy or sample content.
 */
export const HubHero = ({
  name,
  headline,
  subheadline,
  askPlaceholder,
  branding,
  channels,
  questions = [],
  onAsk,
  onTalk,
}: HubHeroProps) => {
  const theme = hubBrandTheme(branding);
  const guideChannel = channels.find(channel => channel.key === "guides");

  return (
    <section className="hub-immersive-hero" style={{ backgroundColor: deepen(theme.primary), backgroundImage: `radial-gradient(ellipse at 95% 0%, rgba(255, 255, 255, 0.10), transparent 60%), radial-gradient(ellipse at 5% 100%, rgba(0, 0, 0, 0.22), transparent 70%), ${heroBackground(theme)}` }}>
      <div className="hub-hero-layout hub-page-width">
        <div className="hub-hero-copy">
          <p className="hub-hero-eyebrow">{name || branding.brandName || "Your Genie"}<span aria-hidden />SUPPORT CENTRE</p>
          <h1>{headline}</h1>
          <p className="hub-hero-description">{subheadline || "Find an answer, follow a guide, or talk it through with your Genie."}</p>
          <div className="hub-hero-channels"><HubChannels channels={channels} tone="onBrand" branding={branding} /></div>
          {guideChannel && <a href="#guides" className="hub-hero-guide-link">Find the right guide <ArrowDown className="h-4 w-4" /></a>}
        </div>
        <div className="hub-hero-genie">
          <div className="hub-hero-avatar" style={{ borderColor: theme.primary, boxShadow: `0 0 0 7px ${tint(theme.primary, 0.07)}` }}>
            {branding.logoUrl ? <img src={branding.logoUrl} alt="" /> : <GenieIcon name="genie-lamp" size={40} color={theme.primary} secondaryColor={theme.secondary} gradientEnabled={theme.gradientOn} label="" />}
          </div>
          <p className="hub-card-eyebrow">YOUR GENIE</p>
          <h2>Where can I help <br />you today?</h2>
          <p>Ask in your own words, <br />or let’s talk it through.</p>
          <HubAskBox placeholder={askPlaceholder} branding={branding} onAsk={onAsk} onTalk={onTalk} />
          {questions.length > 0 && <div className="hub-hero-questions"><p>A FEW PLACES TO START</p>{questions.slice(0, 2).map(question => <button key={question.id} type="button" onClick={() => onAsk(question.question)}>{question.question}<ArrowUpRight className="h-4 w-4 shrink-0" /></button>)}</div>}
        </div>
      </div>
      <div className="hub-hero-footer hub-page-width"><span>HELP, IN YOUR OWN WORDS.</span><span>Ask a question{guideChannel && " · Follow a guide"} · Find your next step</span></div>
    </section>
  );
};

export default HubHero;
